import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiService, ServerDTO, ScheduledMessageDTO, OnlinePlayerDTO } from '@/services/api.service';
import { LogsHistoryModal } from '@/components/LogsHistoryModal';
import { ServerSchematics } from '@/components/ServerSchematics';
import { GameGroupBadge } from '@/components/ui/GameGroupBadge';
import { ServerScopeNotice } from '@/components/supervision/ServerScopeNotice';
import { supportsGameGroups } from '@/config/games';
import { useGameStore } from '@/stores/game.store';
import { useActiveGameGroup } from '@/stores/gameGroups.store';
import { classifyServerFailure, type ApiOutcome, type ServerFailure } from '@/utils/supervision';
import { formatPlayerName } from '@/utils/playerName';
import { sheetUuidOf } from '@/utils/playerSheet';
import styles from './ServerDetailPage.module.scss';

// Mock server data
const MOCK_SERVER: ServerDTO = {
  identifier: 'faction-1',
  uuid: 'uuid-1',
  name: 'Faction 1',
  description: 'Serveur Faction principal',
  status: 'running',
  suspended: false,
  playerCount: 45,
};

// Mock logs pour le developpement
const MOCK_LOGS = [
  '[12:34:56] [Server thread/INFO]: Preparing start region for dimension minecraft:overworld',
  '[12:34:57] [Server thread/INFO]: Preparing spawn area: 0%',
  '[12:34:58] [Server thread/INFO]: Preparing spawn area: 12%',
  '[12:34:59] [Server thread/INFO]: Preparing spawn area: 34%',
  '[12:35:00] [Server thread/INFO]: Preparing spawn area: 67%',
  '[12:35:01] [Server thread/INFO]: Preparing spawn area: 100%',
  '[12:35:02] [Server thread/INFO]: Done (6.234s)! For help, type "help"',
  '[12:35:10] [Server thread/INFO]: Player_123 joined the game',
  '[12:35:15] [Server thread/INFO]: Player_456 joined the game',
  '[12:35:20] [Server thread/WARN]: Can\'t keep up! Is the server overloaded?',
  '[12:35:25] [Server thread/INFO]: Player_789 joined the game',
  '[12:35:30] [Server thread/INFO]: <Player_123> Hello everyone!',
  '[12:35:35] [Server thread/INFO]: Player_123 has made the advancement [Stone Age]',
  '[12:35:40] [Server thread/ERROR]: Failed to save player data for Player_456',
  '[12:35:45] [Server thread/INFO]: Saving chunks for level \'ServerLevel[world]\'',
  '[12:35:50] [Server thread/INFO]: ThreadedAnvilChunkStorage: All chunks saved',
];

interface LogEntry {
  id: number;
  timestamp: Date;
  content: string;
  level: 'INFO' | 'WARN' | 'ERROR' | 'DEBUG';
}

/**
 * Supprime les codes ANSI d'une chaine de caracteres
 * Exemple: [39;49m[37m13:18:58[39;49m -> 13:18:58
 */
const stripAnsiCodes = (text: string): string => {
  // Pattern pour matcher les sequences ANSI escape
  // Supporte: \x1b[...m, \033[...m, et les sequences [XX;XXm sans echappement
  return text
    // Sequences avec escape character (\x1b ou \033)
    .replace(/\x1b\[[0-9;]*m/g, '')
    .replace(/\x1b\[[0-9;]*m/g, '')
    // Sequences sans escape (comme dans l'exemple [39;49m)
    .replace(/\[([0-9;]*)m/g, '')
    // Nettoyer les espaces multiples resultants
    .replace(/\s{2,}/g, ' ')
    .trim();
};

export function ServerDetailPage() {
  const { identifier } = useParams<{ identifier: string }>();
  const navigate = useNavigate();

  const [server, setServer] = useState<ServerDTO | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Groupe de jeu consulté (NationsGlory : Nova, Iris), null sans groupe (Paladium). Constant pendant
  // la vie de la page : un changement de groupe la remonte (clé de l'Outlet dans AdminLayout).
  const activeGroup = useActiveGameGroup();
  // Jeu à groupes (NationsGlory) : jamais de mode démo, qu'un groupe parte ou non avec la requête
  // (liste des groupes jamais chargée : pas d'en-tête, mais la console simulée resterait trompeuse).
  // Un jeu sans groupes (Paladium) garde le mode démo d'avant.
  const activeGameId = useGameStore((s) => s.activeGameId);
  const noDemo = activeGroup !== null || supportsGameGroups(activeGameId);
  // Panne due au groupe (serveur d'un autre groupe, groupe refusé, satellite injoignable) : message
  // et bascule, JAMAIS de console ni de logs simulés, qui feraient croire à une console vivante.
  const [failure, setFailure] = useState<ServerFailure | null>(null);

  // Logs state
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [wsConnected, setWsConnected] = useState(false);
  const [wsError, setWsError] = useState<string | null>(null);
  const [autoScroll, setAutoScroll] = useState(true);
  const [logFilter, setLogFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [useMockData, setUseMockData] = useState(false);

  // Restart modal state
  const [showRestartModal, setShowRestartModal] = useState(false);
  const [restartReasons, setRestartReasons] = useState<string[]>([]);
  const [selectedReason, setSelectedReason] = useState<string>('');
  const [isRestarting, setIsRestarting] = useState(false);
  const [restartError, setRestartError] = useState<string | null>(null);

  // Broadcast modal state
  const [showBroadcastModal, setShowBroadcastModal] = useState(false);
  const [broadcastMessage, setBroadcastMessage] = useState('');
  const [isBroadcasting, setIsBroadcasting] = useState(false);
  const [broadcastError, setBroadcastError] = useState<string | null>(null);

  // Scheduled messages state
  const [scheduledMessages, setScheduledMessages] = useState<ScheduledMessageDTO[]>([]);
  const [showScheduledModal, setShowScheduledModal] = useState(false);
  const [editingScheduled, setEditingScheduled] = useState<ScheduledMessageDTO | null>(null);
  const [scheduledFormMessage, setScheduledFormMessage] = useState('');
  const [scheduledFormInterval, setScheduledFormInterval] = useState(30);
  const [scheduledFormEnabled, setScheduledFormEnabled] = useState(true);
  const [isSavingScheduled, setIsSavingScheduled] = useState(false);
  const [scheduledError, setScheduledError] = useState<string | null>(null);
  const [deletingScheduled, setDeletingScheduled] = useState<ScheduledMessageDTO | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Online players state
  const [onlinePlayers, setOnlinePlayers] = useState<OnlinePlayerDTO[]>([]);

  // Logs history modal state
  const [showLogsHistoryModal, setShowLogsHistoryModal] = useState(false);

  const logsContainerRef = useRef<HTMLDivElement>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const logIdRef = useRef(0);
  const mockIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const hasLoadedRef = useRef(false);
  // Page encore montée : une réponse arrivée après un changement de groupe (page démontée) ne doit
  // ni afficher quoi que ce soit, ni ouvrir une socket console que plus rien ne fermerait.
  const aliveRef = useRef(true);

  useEffect(() => {
    aliveRef.current = true;
    return () => { aliveRef.current = false; };
  }, []);

  useEffect(() => {
    // Eviter le double chargement en React StrictMode
    if (identifier && !hasLoadedRef.current) {
      hasLoadedRef.current = true;
      loadServer(identifier);
    }

    return () => {
      // Cleanup au demontage
      if (mockIntervalRef.current) {
        clearInterval(mockIntervalRef.current);
        mockIntervalRef.current = null;
      }
    };
  }, [identifier]);

  // Load online players and refresh every 15 seconds
  useEffect(() => {
    if (server?.name) {
      loadOnlinePlayers();
      const interval = setInterval(loadOnlinePlayers, 15000);
      return () => clearInterval(interval);
    }
  }, [server?.name]);

  const loadOnlinePlayers = async () => {
    const result = await apiService.getOnlinePlayers();
    if (result.data) {
      // Filter players on this server
      const serverName = server?.name?.toLowerCase() || '';
      const playersOnServer = result.data.players.filter((p) => {
        const playerServer = p.serverName?.toLowerCase() || '';
        return playerServer === serverName ||
               playerServer.includes(serverName) ||
               serverName.includes(playerServer);
      });
      setOnlinePlayers(playersOnServer);
    }
  };

  // Auto-scroll effect
  useEffect(() => {
    if (autoScroll && logsContainerRef.current) {
      logsContainerRef.current.scrollTop = logsContainerRef.current.scrollHeight;
    }
  }, [logs, autoScroll]);

  // Cleanup WebSocket on unmount
  useEffect(() => {
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, []);

  const loadServer = async (serverId: string) => {
    setLoading(true);
    setError(null);
    setFailure(null);

    // request() renvoie aussi status et code (404 de périmètre du satellite, refus de groupe).
    const result: { data?: ServerDTO } & ApiOutcome = await apiService.getServer(serverId);
    if (!aliveRef.current) return;

    const failed = classifyServerFailure(result, noDemo);
    if (failed) {
      // Erreur de périmètre, de groupe ou de cible : jamais le mode démo.
      setFailure(failed);
      setServer(null);
      setUseMockData(false);
    } else if (result.error) {
      // Utiliser mock data
      console.warn('API failed, using mock data:', result.error);
      setServer({ ...MOCK_SERVER, identifier: serverId, name: serverId });
      setUseMockData(true);
      loadMockLogs();
    } else if (result.data) {
      setServer(result.data);
      setUseMockData(false);
      // Connecter au WebSocket pour les vrais logs
      connectWebSocket(serverId);
    }

    setLoading(false);
  };

  const loadMockLogs = () => {
    // Eviter de charger les logs mock plusieurs fois
    if (mockIntervalRef.current) {
      return;
    }

    // Simuler des logs en mode demo
    const initialLogs: LogEntry[] = MOCK_LOGS.map((content) => ({
      id: logIdRef.current++,
      timestamp: new Date(),
      content,
      level: getLogLevel(content),
    }));
    setLogs(initialLogs);
    setWsConnected(true);

    // Simuler l'arrivee de nouveaux logs
    mockIntervalRef.current = setInterval(() => {
      const randomLog = MOCK_LOGS[Math.floor(Math.random() * MOCK_LOGS.length)];
      if (!randomLog) return;
      const now = new Date();
      const timeStr = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}:${String(now.getSeconds()).padStart(2, '0')}`;
      const newContent = randomLog.replace(/\[\d{2}:\d{2}:\d{2}\]/, `[${timeStr}]`);

      setLogs((prev) => {
        const newLogs = [
          ...prev,
          {
            id: logIdRef.current++,
            timestamp: now,
            content: newContent,
            level: getLogLevel(newContent),
          },
        ];
        // Garder seulement les 500 derniers logs
        return newLogs.slice(-500);
      });
    }, 2000 + Math.random() * 3000);
  };

  const connectWebSocket = async (serverId: string) => {
    // Eviter les connexions multiples
    if (wsRef.current && wsRef.current.readyState !== WebSocket.CLOSED) {
      return;
    }

    try {
      // Ticket a usage unique (30 s) : une WebSocket de navigateur ne peut pas porter d'en-tete
      // Authorization. On ne recoit plus de token Pterodactyl, et on ne parle plus qu'au panel.
      const result: Awaited<ReturnType<typeof apiService.createConsoleTicket>> & ApiOutcome =
        await apiService.createConsoleTicket(serverId);
      if (!aliveRef.current) return; // page démontée pendant le ticket : pas de socket orpheline

      if (result.error || !result.data) {
        const message = result.error ?? "Impossible d'ouvrir la console";
        setWsError(message);
        // Groupe de jeu : la console reste vide avec le motif, jamais de logs simulés.
        if (classifyServerFailure({ ...result, error: message }, noDemo)) return;
        loadMockLogs(); // Fallback vers mock
        setUseMockData(true);
        return;
      }

      // Relais : navigateur -> panel -> satellite du projet -> Pterodactyl.
      // Le satellite fait l'authentification Pterodactyl, il n'y a donc plus d'evenement
      // 'auth' a envoyer ici ; les evenements recus restent ceux de Pterodactyl.
      const ws = new WebSocket(apiService.consoleStreamUrl(result.data.ticket));
      wsRef.current = ws;

      ws.onopen = () => {
        console.log('Console relay connected');
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);

          if (data.event === 'panel error') {
            // Emis par le relais lui-meme (ticket invalide, satellite ou Pterodactyl injoignable),
            // et non par Pterodactyl : on affiche la raison telle quelle, elle est explicite.
            setWsError(data.args?.[0] ?? 'Console indisponible');
            setWsConnected(false);
          } else if (data.event === 'auth success') {
            setWsConnected(true);
            setWsError(null);
            // Demander les logs
            ws.send(JSON.stringify({
              event: 'send logs',
              args: [null],
            }));
          } else if (data.event === 'console output') {
            const rawContent = data.args[0];
            if (rawContent) {
              // Parser les codes ANSI pour un affichage propre
              const content = stripAnsiCodes(rawContent);
              if (content) { // Ignorer les lignes vides apres parsing
                setLogs((prev) => {
                  const newLogs = [
                    ...prev,
                    {
                      id: logIdRef.current++,
                      timestamp: new Date(),
                      content,
                      level: getLogLevel(content),
                    },
                  ];
                  return newLogs.slice(-500);
                });
              }
            }
          } else if (data.event === 'status') {
            // Mise a jour du statut du serveur
            const status = data.args[0];
            setServer((prev) => prev ? { ...prev, status } : null);
          }
        } catch (e) {
          console.error('Failed to parse WebSocket message:', e);
        }
      };

      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
        setWsError('Erreur de connexion WebSocket');
      };

      ws.onclose = () => {
        console.log('WebSocket closed');
        setWsConnected(false);
      };
    } catch (e) {
      console.error('Failed to connect WebSocket:', e);
      setWsError('Erreur lors de la connexion');
      if (noDemo) return; // jeu à groupes : pas de logs simulés
      loadMockLogs();
      setUseMockData(true);
    }
  };

  const getLogLevel = (content: string): LogEntry['level'] => {
    const lowerContent = content.toLowerCase();
    // Format Java: [Server thread/ERROR]
    // Format Bedrock: [Error] ou [ERROR]
    if (lowerContent.includes('/error]') || lowerContent.includes('[error]') || lowerContent.includes('/fatal]')) {
      return 'ERROR';
    }
    if (lowerContent.includes('/warn]') || lowerContent.includes('[warn]') || lowerContent.includes('[warning]')) {
      return 'WARN';
    }
    if (lowerContent.includes('/debug]') || lowerContent.includes('[debug]')) {
      return 'DEBUG';
    }
    return 'INFO';
  };

  const getLogLevelClass = (level: LogEntry['level']) => {
    switch (level) {
      case 'ERROR':
        return styles.logError;
      case 'WARN':
        return styles.logWarn;
      case 'DEBUG':
        return styles.logDebug;
      default:
        return styles.logInfo;
    }
  };

  const filteredLogs = logs.filter((log) => {
    // Filtre par niveau
    if (logFilter !== 'all' && log.level.toLowerCase() !== logFilter) {
      return false;
    }
    // Filtre par recherche
    if (searchQuery && !log.content.toLowerCase().includes(searchQuery.toLowerCase())) {
      return false;
    }
    return true;
  });

  const clearLogs = () => {
    setLogs([]);
  };

  const handleScroll = () => {
    if (logsContainerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = logsContainerRef.current;
      // Desactiver auto-scroll si l'utilisateur scrolle vers le haut
      const isAtBottom = scrollHeight - scrollTop - clientHeight < 50;
      setAutoScroll(isAtBottom);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'running':
        return styles.statusRunning;
      case 'stopped':
        return styles.statusStopped;
      case 'starting':
        return styles.statusStarting;
      default:
        return styles.statusUnknown;
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'running':
        return 'En ligne';
      case 'stopped':
        return 'Arrêté';
      case 'starting':
        return 'Démarrage...';
      default:
        return 'Inconnu';
    }
  };

  // Restart functions
  const openRestartModal = async () => {
    setRestartError(null);
    setSelectedReason('');

    // Charger les motifs de restart si pas deja fait
    if (restartReasons.length === 0) {
      const result = await apiService.getRestartReasons();
      if (result.data) {
        setRestartReasons(result.data.reasons);
        if (result.data.reasons.length > 0 && result.data.reasons[0]) {
          setSelectedReason(result.data.reasons[0]);
        }
      }
    } else if (restartReasons.length > 0 && restartReasons[0]) {
      setSelectedReason(restartReasons[0]);
    }

    setShowRestartModal(true);
  };

  const handleRestart = async () => {
    if (!identifier || !selectedReason) return;

    setIsRestarting(true);
    setRestartError(null);

    const result = await apiService.restartServer(identifier, selectedReason);

    if (result.error) {
      setRestartError(result.error);
      setIsRestarting(false);
    } else {
      setShowRestartModal(false);
      setIsRestarting(false);
      // Le statut sera mis a jour via le WebSocket
    }
  };

  // Broadcast functions
  const openBroadcastModal = () => {
    setBroadcastError(null);
    setBroadcastMessage('');
    setShowBroadcastModal(true);
  };

  const handleBroadcast = async () => {
    if (!identifier || !broadcastMessage.trim()) return;

    setIsBroadcasting(true);
    setBroadcastError(null);

    const result = await apiService.broadcastMessage(identifier, broadcastMessage.trim());

    if (result.error) {
      setBroadcastError(result.error);
      setIsBroadcasting(false);
    } else {
      setShowBroadcastModal(false);
      setIsBroadcasting(false);
      setBroadcastMessage('');
    }
  };

  // Scheduled messages functions
  const loadScheduledMessages = async () => {
    if (!identifier) return;
    const result = await apiService.getScheduledMessages(identifier);
    if (result.data) {
      setScheduledMessages(result.data.messages);
    }
  };

  const openScheduledModal = (message?: ScheduledMessageDTO) => {
    setScheduledError(null);
    if (message) {
      setEditingScheduled(message);
      setScheduledFormMessage(message.message);
      setScheduledFormInterval(message.intervalMinutes);
      setScheduledFormEnabled(message.enabled);
    } else {
      setEditingScheduled(null);
      setScheduledFormMessage('');
      setScheduledFormInterval(30);
      setScheduledFormEnabled(true);
    }
    setShowScheduledModal(true);
  };

  const handleSaveScheduled = async () => {
    if (!identifier || !scheduledFormMessage.trim()) return;

    setIsSavingScheduled(true);
    setScheduledError(null);

    let result;
    if (editingScheduled) {
      result = await apiService.updateScheduledMessage(
        identifier,
        editingScheduled.id,
        scheduledFormMessage.trim(),
        scheduledFormInterval,
        scheduledFormEnabled
      );
    } else {
      result = await apiService.createScheduledMessage(
        identifier,
        scheduledFormMessage.trim(),
        scheduledFormInterval
      );
    }

    if (result.error) {
      setScheduledError(result.error);
      setIsSavingScheduled(false);
    } else {
      setShowScheduledModal(false);
      setIsSavingScheduled(false);
      loadScheduledMessages();
    }
  };

  const handleToggleScheduled = async (msg: ScheduledMessageDTO) => {
    if (!identifier) return;

    // Optimistic update pour un feedback immédiat
    const newEnabledState = !msg.enabled;
    setScheduledMessages(prev =>
      prev.map(m => m.id === msg.id ? { ...m, enabled: newEnabledState } : m)
    );

    const result = await apiService.toggleScheduledMessage(identifier, msg.id, newEnabledState);

    if (result.error) {
      // Revert si erreur
      console.error('Failed to toggle scheduled message:', result.error);
      setScheduledMessages(prev =>
        prev.map(m => m.id === msg.id ? { ...m, enabled: msg.enabled } : m)
      );
      // Recharger depuis le serveur pour être sûr d'avoir l'état correct
      loadScheduledMessages();
    }
  };

  const handleDeleteScheduled = async () => {
    if (!identifier || !deletingScheduled) return;

    setIsDeleting(true);
    await apiService.deleteScheduledMessage(identifier, deletingScheduled.id);
    setIsDeleting(false);
    setDeletingScheduled(null);
    loadScheduledMessages();
  };

  const formatInterval = (minutes: number): string => {
    if (minutes < 60) return `Toutes les ${minutes} min`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours === 1 && mins === 0) return `Toutes les heures`;
    if (mins === 0) return `Toutes les ${hours}h`;
    return `Toutes les ${hours}h ${mins}min`;
  };

  const formatIntervalShort = (minutes: number): string => {
    if (minutes < 60) return `${minutes}min`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (mins === 0) return `${hours}h`;
    return `${hours}h${mins}`;
  };

  const formatConnectedTime = (timestamp: number): string => {
    const now = Date.now();
    const diff = now - timestamp;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(minutes / 60);

    if (hours > 0) {
      return `${hours}h ${minutes % 60}m`;
    }
    return `${minutes}m`;
  };

  // Charger les messages programmés au montage
  useEffect(() => {
    if (identifier && server) {
      loadScheduledMessages();
    }
  }, [identifier, server]);

  if (loading) {
    return (
      <div className={styles.page}>
        <div className={styles.loading}>
          <div className={styles.spinner} />
        </div>
      </div>
    );
  }

  if (failure) {
    return (
      <div className={styles.page}>
        <ServerScopeNotice
          failure={failure}
          identifier={identifier ?? ''}
          onRetry={() => { if (identifier) void loadServer(identifier); }}
          onBack={() => navigate('/console')}
        />
      </div>
    );
  }

  if (!server) {
    return (
      <div className={styles.page}>
        <div className={styles.errorState}>
          <span className={styles.errorIcon}>❌</span>
          <h2>Serveur introuvable</h2>
          <p>Le serveur "{identifier}" n'existe pas ou vous n'avez pas les permissions.</p>
          <button className={styles.backButton} onClick={() => navigate('/console')}>
            Retour à la liste
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      {/* Header */}
      <header className={styles.header}>
        <button className={styles.backLink} onClick={() => navigate('/console')}>
          ← Retour aux serveurs
        </button>

        <div className={styles.serverInfo}>
          <div className={styles.serverMain}>
            <h1 className={styles.serverName}>{server.name}</h1>
            <span className={`${styles.statusBadge} ${getStatusColor(server.status)}`}>
              {getStatusLabel(server.status)}
            </span>
            <GameGroupBadge size="md" />
            {useMockData && (
              <span className={styles.mockBadge}>Mode Demo</span>
            )}
          </div>
          <div className={styles.serverMeta}>
            <span className={styles.identifier}>{server.identifier}</span>
            <span className={styles.separator}>•</span>
            <span className={styles.playerCount}>
              👥 {onlinePlayers.length} joueur{onlinePlayers.length !== 1 ? 's' : ''}
            </span>
          </div>
        </div>

        <div className={styles.serverActions}>
          <button
            className={styles.broadcastButton}
            onClick={openBroadcastModal}
            disabled={server.status?.toLowerCase() !== 'running'}
          >
            📢 Message
          </button>
          <button
            className={styles.restartButton}
            onClick={openRestartModal}
            disabled={server.status?.toLowerCase() !== 'running'}
          >
            Redémarrer
          </button>
        </div>
      </header>

      {/* Broadcast Modal */}
      {showBroadcastModal && (
        <div className={styles.modalOverlay} onClick={() => setShowBroadcastModal(false)}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <h3 className={styles.modalTitle}>Envoyer un message</h3>
            <GameGroupBadge block caption="Groupe de jeu" />
            <p className={styles.modalDescription}>
              Envoyez un message à tous les joueurs connectés sur <strong>{server.name}</strong>.
            </p>

            <div className={styles.modalField}>
              <label className={styles.modalLabel}>Message</label>
              <textarea
                className={styles.modalTextarea}
                value={broadcastMessage}
                onChange={(e) => setBroadcastMessage(e.target.value)}
                placeholder="Votre message..."
                maxLength={500}
                rows={4}
              />
              <span className={styles.charCount}>{broadcastMessage.length}/500</span>
            </div>

            {broadcastError && (
              <div className={styles.modalError}>{broadcastError}</div>
            )}

            <div className={styles.modalActions}>
              <button
                className={styles.modalCancelButton}
                onClick={() => setShowBroadcastModal(false)}
                disabled={isBroadcasting}
              >
                Annuler
              </button>
              <button
                className={styles.modalConfirmButtonPrimary}
                onClick={handleBroadcast}
                disabled={isBroadcasting || !broadcastMessage.trim()}
              >
                {isBroadcasting ? 'Envoi...' : 'Envoyer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Restart Modal */}
      {showRestartModal && (
        <div className={styles.modalOverlay} onClick={() => setShowRestartModal(false)}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <h3 className={styles.modalTitle}>Redémarrer le serveur</h3>
            <GameGroupBadge block caption="Groupe de jeu" />
            <p className={styles.modalDescription}>
              Sélectionnez un motif pour le redémarrage du serveur <strong>{server.name}</strong>.
              Tous les joueurs seront kickés avant le redémarrage.
            </p>

            <div className={styles.modalField}>
              <label className={styles.modalLabel}>Motif du redémarrage</label>
              <select
                className={styles.modalSelect}
                value={selectedReason}
                onChange={(e) => setSelectedReason(e.target.value)}
              >
                {restartReasons.map((reason) => (
                  <option key={reason} value={reason}>{reason}</option>
                ))}
              </select>
            </div>

            {restartError && (
              <div className={styles.modalError}>{restartError}</div>
            )}

            <div className={styles.modalActions}>
              <button
                className={styles.modalCancelButton}
                onClick={() => setShowRestartModal(false)}
                disabled={isRestarting}
              >
                Annuler
              </button>
              <button
                className={styles.modalConfirmButton}
                onClick={handleRestart}
                disabled={isRestarting || !selectedReason}
              >
                {isRestarting ? 'Redémarrage...' : 'Confirmer le redémarrage'}
              </button>
            </div>
          </div>
        </div>
      )}

      {error && <div className={styles.errorBanner}>{error}</div>}
      {wsError && <div className={styles.warnBanner}>{wsError}</div>}

      {/* Main Content Grid */}
      <div className={styles.mainContent}>
        {/* Console */}
        <section className={styles.consoleSection}>
          <div className={styles.consoleHeader}>
            <h2 className={styles.consoleTitle}>
              Console
              <span className={`${styles.connectionStatus} ${wsConnected ? styles.connected : styles.disconnected}`}>
                {wsConnected ? 'Connecté' : 'Déconnecté'}
              </span>
            </h2>

            <div className={styles.consoleControls}>
              <input
                type="text"
                className={styles.searchInput}
                placeholder="Filtrer les logs..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <select
                className={styles.levelSelect}
                value={logFilter}
                onChange={(e) => setLogFilter(e.target.value)}
              >
                <option value="all">Tous les niveaux</option>
                <option value="info">INFO</option>
                <option value="warn">WARN</option>
                <option value="error">ERROR</option>
              </select>
              <button className={styles.clearButton} onClick={clearLogs}>
                Effacer
              </button>
              <button className={styles.historyButton} onClick={() => setShowLogsHistoryModal(true)}>
                Historique
              </button>
              <label className={styles.autoScrollLabel}>
                <input
                  type="checkbox"
                  checked={autoScroll}
                  onChange={(e) => setAutoScroll(e.target.checked)}
                />
                Auto-scroll
              </label>
            </div>
          </div>

          <div
            ref={logsContainerRef}
            className={styles.logsContainer}
            onScroll={handleScroll}
          >
            {filteredLogs.length === 0 ? (
              <div className={styles.noLogs}>
                {logs.length === 0 ? 'En attente des logs...' : 'Aucun log correspondant aux filtres'}
              </div>
            ) : (
              filteredLogs.map((log) => (
                <div key={log.id} className={`${styles.logLine} ${getLogLevelClass(log.level)}`}>
                  <span className={styles.logContent}>{log.content}</span>
                </div>
              ))
            )}
          </div>

          <div className={styles.consoleFooter}>
            <span className={styles.logCount}>
              {filteredLogs.length} / {logs.length} lignes
            </span>
          </div>
        </section>

        {/* Right Sidebar */}
        <div className={styles.rightSidebar}>
          {/* Online Players Section */}
          <section className={styles.playersSidebar}>
            <div className={styles.playersHeader}>
              <div className={styles.playersTitleWrapper}>
                <span className={styles.playersIcon}>👥</span>
                <h2 className={styles.playersTitle}>Joueurs en ligne</h2>
              </div>
              <span className={styles.playersCountBadge}>{onlinePlayers.length}</span>
            </div>

            <div className={styles.playersContent}>
              {onlinePlayers.length === 0 ? (
                <div className={styles.playersEmpty}>
                  <span className={styles.emptyIcon}>👻</span>
                  <p>Aucun joueur connecté</p>
                </div>
              ) : (
                <div className={styles.playersList}>
                  {onlinePlayers.map((player) => (
                    <div
                      key={player.uuid}
                      className={styles.playerItem}
                      onClick={() => {
                        // L'uuid du joueur en ligne suffit (canonique depuis la v1.0.13 du satellite).
                        void sheetUuidOf(player).then((uuid) => navigate(`/players/${uuid}`));
                      }}
                    >
                      <div className={styles.playerItemLeft}>
                        <img
                          src="https://mc-heads.net/avatar/MHF_Steve/24"
                          alt=""
                          className={styles.playerItemAvatar}
                        />
                        <div className={styles.playerItemInfo}>
                          <span className={styles.playerItemName}>
                            {player.isStaff && <span className={styles.staffBadge}>Staff</span>}
                            {formatPlayerName(player.name, player.displayName)}
                          </span>
                        </div>
                      </div>
                      <span className={styles.playerItemTime}>
                        {formatConnectedTime(player.connectedSince)}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </section>

          {/* Scheduled Messages Section */}
          <aside className={styles.scheduledSidebar}>
          <div className={styles.scheduledHeader}>
            <div className={styles.scheduledTitleWrapper}>
              <span className={styles.scheduledIcon}>📅</span>
              <h2 className={styles.scheduledTitle}>Messages auto</h2>
            </div>
            <div className={styles.addButtonWrapper}>
              <button
                className={styles.addScheduledButton}
                onClick={() => openScheduledModal()}
                disabled={server.status?.toLowerCase() !== 'running'}
              >
                + Ajouter
              </button>
              {server.status?.toLowerCase() !== 'running' && (
                <span className={styles.disabledHint}>Serveur hors ligne</span>
              )}
            </div>
          </div>

          <div className={styles.scheduledContent}>
            {scheduledMessages.length === 0 ? (
              <div className={styles.scheduledEmpty}>
                <span className={styles.emptyIcon}>📭</span>
                <p>Aucun message programmé</p>
                <span className={styles.emptyHint}>
                  Créez des messages qui s'enverront automatiquement aux joueurs.
                </span>
              </div>
            ) : (
              <div className={styles.scheduledList}>
                {scheduledMessages.map((msg) => (
                  <div key={msg.id} className={`${styles.scheduledItem} ${!msg.enabled ? styles.scheduledDisabled : ''}`}>
                    <div className={styles.scheduledItemHeader}>
                      <span className={`${styles.statusDot} ${msg.enabled ? styles.statusActive : styles.statusInactive}`} />
                      <span className={styles.scheduledIntervalBadge}>
                        <span className={styles.intervalIcon}>⏱</span>
                        {formatIntervalShort(msg.intervalMinutes)}
                      </span>
                    </div>
                    <p className={styles.scheduledMessage}>{msg.message}</p>
                    {msg.lastSentAt && (
                      <span className={styles.scheduledLastSent}>
                        Dernier : {new Date(msg.lastSentAt).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    )}
                    <div className={styles.scheduledActions}>
                      <button
                        className={`${styles.scheduledToggle} ${msg.enabled ? styles.toggleOn : styles.toggleOff}`}
                        onClick={() => handleToggleScheduled(msg)}
                        title={msg.enabled ? 'Désactiver' : 'Activer'}
                      >
                        {msg.enabled ? '⏸' : '▶'}
                      </button>
                      <button
                        className={styles.scheduledEdit}
                        onClick={() => openScheduledModal(msg)}
                        title="Modifier"
                      >
                        ✏️
                      </button>
                      <button
                        className={styles.scheduledDelete}
                        onClick={() => setDeletingScheduled(msg)}
                        title="Supprimer"
                      >
                        🗑️
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className={styles.scheduledFooter}>
            <span className={styles.scheduledCount}>
              {scheduledMessages.filter(m => m.enabled).length} actif{scheduledMessages.filter(m => m.enabled).length !== 1 ? 's' : ''} / {scheduledMessages.length} total
            </span>
          </div>
        </aside>
        </div>

        {identifier && <ServerSchematics identifier={identifier} />}
      </div>

      {/* Scheduled Message Modal */}
      {showScheduledModal && (
        <div className={styles.modalOverlay} onClick={() => setShowScheduledModal(false)}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <h3 className={styles.modalTitle}>
              {editingScheduled ? 'Modifier le message' : 'Nouveau message programmé'}
            </h3>
            <p className={styles.modalDescription}>
              Ce message sera envoyé automatiquement à tous les joueurs selon l'intervalle défini.
            </p>

            <div className={styles.modalField}>
              <label className={styles.modalLabel}>Message</label>
              <textarea
                className={styles.modalTextarea}
                value={scheduledFormMessage}
                onChange={(e) => setScheduledFormMessage(e.target.value)}
                placeholder="Votre message..."
                maxLength={500}
                rows={3}
              />
              <span className={styles.charCount}>{scheduledFormMessage.length}/500</span>
            </div>

            <div className={styles.modalField}>
              <label className={styles.modalLabel}>Intervalle</label>
              <div className={styles.intervalSelector}>
                <select
                  className={styles.modalSelect}
                  value={scheduledFormInterval}
                  onChange={(e) => setScheduledFormInterval(Number(e.target.value))}
                >
                  <option value={5}>5 minutes</option>
                  <option value={10}>10 minutes</option>
                  <option value={15}>15 minutes</option>
                  <option value={30}>30 minutes</option>
                  <option value={60}>1 heure</option>
                  <option value={120}>2 heures</option>
                  <option value={180}>3 heures</option>
                  <option value={360}>6 heures</option>
                  <option value={720}>12 heures</option>
                  <option value={1440}>24 heures</option>
                </select>
              </div>
            </div>

            {editingScheduled && (
              <div className={styles.modalField}>
                <label className={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={scheduledFormEnabled}
                    onChange={(e) => setScheduledFormEnabled(e.target.checked)}
                  />
                  Actif
                </label>
              </div>
            )}

            {scheduledError && (
              <div className={styles.modalError}>{scheduledError}</div>
            )}

            <div className={styles.modalActions}>
              <button
                className={styles.modalCancelButton}
                onClick={() => setShowScheduledModal(false)}
                disabled={isSavingScheduled}
              >
                Annuler
              </button>
              <button
                className={styles.modalConfirmButtonPrimary}
                onClick={handleSaveScheduled}
                disabled={isSavingScheduled || !scheduledFormMessage.trim()}
              >
                {isSavingScheduled ? 'Enregistrement...' : (editingScheduled ? 'Modifier' : 'Créer')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deletingScheduled && (
        <div className={styles.modalOverlay} onClick={() => setDeletingScheduled(null)}>
          <div className={styles.modalSmall} onClick={(e) => e.stopPropagation()}>
            <div className={styles.deleteModalIcon}>🗑️</div>
            <h3 className={styles.deleteModalTitle}>Supprimer ce message ?</h3>
            <p className={styles.deleteModalDescription}>
              Cette action est irréversible. Le message programmé sera définitivement supprimé.
            </p>
            <div className={styles.deleteModalPreview}>
              <p className={styles.deleteModalMessage}>"{deletingScheduled.message}"</p>
              <div className={styles.deleteModalIntervalWrapper}>
                <span className={styles.deleteModalIntervalIcon}>⏱</span>
                <span className={styles.deleteModalIntervalText}>
                  {formatInterval(deletingScheduled.intervalMinutes)}
                </span>
              </div>
            </div>
            <div className={styles.modalActions}>
              <button
                className={styles.modalCancelButton}
                onClick={() => setDeletingScheduled(null)}
                disabled={isDeleting}
              >
                Annuler
              </button>
              <button
                className={styles.modalConfirmButtonDanger}
                onClick={handleDeleteScheduled}
                disabled={isDeleting}
              >
                {isDeleting ? 'Suppression...' : 'Supprimer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Logs History Modal */}
      {server && (
        <LogsHistoryModal
          isOpen={showLogsHistoryModal}
          onClose={() => setShowLogsHistoryModal(false)}
          serverIdentifier={server.identifier}
          serverName={server.name}
        />
      )}
    </div>
  );
}
